/**
 * Guest-onboarding completion flag.
 *
 * Purpose:
 *   Track whether an *anonymous* visitor (no account) has already seen the
 *   onboarding/guide. Once they have, the Guide button in the topbar is
 *   hidden so guests cannot manually replay the onboarding and accidentally
 *   bypass the 15-free-recipes limit or any other guest-tier restriction.
 *
 * Scope:
 *   This flag is ONLY consulted for unauthenticated visitors. Signed-in
 *   users have their `onboarding_completed` flag persisted server-side and
 *   keep access to the Guide button as a deliberate UX choice.
 *
 *   In addition, a *session-scoped* flag (`SESSION_KEY`) records whether
 *   the user is currently in the very first session in which they did
 *   their onboarding. While that session is active the Guide button stays
 *   visible so the user can replay or step back through the guide. Only
 *   on the NEXT session (new tab / new login) does the button disappear.
 */

const STORAGE_KEY = 'guest_onboarding_completed_v1';
const SESSION_KEY = 'guest_onboarding_first_session_v1';
const EVENT_NAME = 'slushbook:guest-onboarding-changed';

export function isGuestOnboardingCompleted() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch (_e) {
    return false;
  }
}

export function markGuestOnboardingCompleted() {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
    window.dispatchEvent(new Event(EVENT_NAME));
  } catch (_e) {
    /* ignore — quota / private mode */
  }
}

export function clearGuestOnboardingCompleted() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new Event(EVENT_NAME));
  } catch (_e) {
    /* ignore */
  }
}

/**
 * sessionStorage-backed "this is the user's first onboarding session" flag.
 * Cleared automatically when the tab/window closes. We also clear it on
 * logout so a subsequent login starts a fresh "post-onboarding" session
 * where the Guide button is hidden.
 */
export function markFirstOnboardingSession() {
  try {
    sessionStorage.setItem(SESSION_KEY, 'true');
    window.dispatchEvent(new Event(EVENT_NAME));
  } catch (_e) {
    /* ignore */
  }
}

export function isFirstOnboardingSession() {
  try {
    return sessionStorage.getItem(SESSION_KEY) === 'true';
  } catch (_e) {
    return false;
  }
}

export function clearFirstOnboardingSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
    window.dispatchEvent(new Event(EVENT_NAME));
  } catch (_e) {
    /* ignore */
  }
}

export const GUEST_ONBOARDING_EVENT = EVENT_NAME;
